'use client';

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { commonClient } from '@bip/api-client';

const COOKIE_NAME = 'tp_currency';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift()!.trim() || null;
  return null;
}

interface CurrencyContextValue {
  currencyCode: string;
  currencySymbol: string;
  convertPrice: (amount: number, fromCurrency: string) => number;
  formatPrice: (amount: number, fromCurrency: string) => string;
  /** Format an amount in the given currency using the CMS glyph, WITHOUT converting.
   *  Use for "you'll pay in <property currency>" amounts that must stay in their
   *  original currency — formatPrice would re-convert them to the user's currency. */
  formatInCurrency: (amount: number, code: string) => string;
  isReady: boolean;
  /** Raw exchange-rate map as returned by the API. Key = foreign currency code,
   *  value = how many units of that currency equal 1 unit of the user's currency.
   *  e.g. { EUR: 1.17 } when user currency is GBP → 1 GBP = 1.17 EUR. */
  exchangeRates: Record<string, number>;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currencyCode: 'GBP',
  currencySymbol: '£',
  convertPrice: (amount) => amount,
  formatPrice: (amount, fromCurrency) =>
    `${(fromCurrency || 'GBP').toUpperCase()} ${(amount || 0).toFixed(2)}`,
  formatInCurrency: (amount, code) =>
    `${(code || 'GBP').toUpperCase()} ${(amount || 0).toFixed(2)}`,
  isReady: false,
  exchangeRates: {},
});

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({
  children,
  // SSR seed (root layout reads the `tp_currency` cookie + server-fetches the symbol map + FX rates,
  // passing them here) so the FIRST render is already in the user's currency + converted amount — no
  // GBP→user flicker. When absent/empty (server fetch failed, or a build-time prerender with no cookie),
  // the client useEffect below still resolves everything as before.
  initialCurrency,
  initialSymbol,
  initialRates,
  initialSymbolByCode,
}: {
  children: React.ReactNode;
  initialCurrency?: string;
  initialSymbol?: string;
  initialRates?: Record<string, number>;
  initialSymbolByCode?: Record<string, string>;
}) {
  // "Seeded" = the server handed us usable FX rates → skip the client fetch entirely on first load.
  const seeded = !!initialRates && Object.keys(initialRates).length > 0;
  const [currencyCode, setCurrencyCode] = useState(initialCurrency || 'GBP');
  const [currencySymbol, setCurrencySymbol] = useState(initialSymbol || '£');
  // rates map: { "EUR": 1.17, "USD": 1.27 } means 1 userCurrency = X foreignCurrency
  const [rates, setRates] = useState<Record<string, number>>(initialRates || {});
  // Full {code → symbol} map sourced from CustomCollection_CountryData_B8ZO3
  // via /currency/list. This is what powers symbol rendering for *any*
  // currency the app might display (user's own, or a foreign currency we
  // failed to convert). We deliberately do NOT use Intl.NumberFormat for
  // symbols because it misses BHD / KWD / OMR / etc. — the CMS has the
  // authoritative glyph for every supported currency.
  const [symbolByCode, setSymbolByCode] = useState<Record<string, string>>(initialSymbolByCode || {});
  const [isReady, setIsReady] = useState(seeded);

  useEffect(() => {
    // Already seeded from SSR (currency + rates + symbols) → nothing to fetch; skip so we never
    // overwrite the correct first-paint values (and never trigger a needless re-render/flicker).
    if (seeded) return;
    let cancelled = false;

    async function init() {
      const cookieCurrency = getCookie(COOKIE_NAME);
      const userCurrency =
        cookieCurrency && /^[A-Za-z]{3}$/.test(cookieCurrency.trim())
          ? cookieCurrency.trim().toUpperCase()
          : 'GBP';

      if (!cancelled) setCurrencyCode(userCurrency);

      // Fetch the full currency list + the rates table in parallel.
      // The list gives us symbols for every currency the CMS knows about;
      // we no longer need a separate /by-code call because the list already
      // contains the user's selection.
      const [listRes, ratesRes] = await Promise.allSettled([
        commonClient.getCurrencyList(),
        commonClient.getExchangeRates(userCurrency),
      ]);

      if (cancelled) return;

      if (listRes.status === 'fulfilled' && listRes.value?.message === 'success' && Array.isArray(listRes.value.data)) {
        const map: Record<string, string> = {};
        for (const c of listRes.value.data) {
          if (c?.CurrencyCode && c?.CurrencySymbol) {
            map[c.CurrencyCode.toUpperCase()] = c.CurrencySymbol;
          }
        }
        setSymbolByCode(map);

        const userEntry = map[userCurrency];
        if (userEntry) {
          setCurrencySymbol(userEntry);
        } else {
          // CMS has no row for this currency — show the code so the price
          // is at least unambiguous.
          setCurrencySymbol(userCurrency);
        }
      } else {
        // common-service unavailable — show the ISO code rather than guess.
        setCurrencySymbol(userCurrency);
      }

      if (ratesRes.status === 'fulfilled' && ratesRes.value?.message === 'success' && ratesRes.value.data?.rates) {
        setRates(ratesRes.value.data.rates);
      }

      setIsReady(true);
    }

    init();
    return () => { cancelled = true; };
  }, [seeded]);

  /**
   * Convert an amount from its original currency to the user's selected currency.
   *
   * The rates object from the API is: { "EUR": 1.17, "USD": 1.27 }
   * meaning 1 unit of userCurrency (e.g. GBP) = 1.17 EUR.
   *
   * So to convert FROM EUR TO GBP: amount / rates["EUR"]
   * e.g. 100 EUR / 1.17 = 85.47 GBP
   */
  const convertPrice = useCallback(
    (amount: number, fromCurrency: string): number => {
      if (!amount || !fromCurrency) return amount;
      // Same currency — no conversion needed
      if (fromCurrency.toUpperCase() === currencyCode.toUpperCase()) return amount;

      const rate = rates[fromCurrency.toUpperCase()] || rates[fromCurrency];
      if (rate && rate > 0) {
        // rate = how many "fromCurrency" units per 1 "userCurrency"
        // so: amountInUserCurrency = amountInFromCurrency / rate
        return Math.round((amount / rate) * 100) / 100;
      }

      // No rate available — return original amount
      return amount;
    },
    [currencyCode, rates],
  );

  /**
   * Format an amount, converting from its original currency to the user's
   * selected currency. Falls back to displaying in the original currency if
   * no exchange rate is available.
   *
   * Symbols come from `CustomCollection_CountryData_B8ZO3` (loaded via
   * /currency/list at provider init) — never from Intl. Intl's narrowSymbol
   * is missing glyphs for BHD/KWD/OMR/etc. and silently returns the ISO
   * code, so going through it drops the CMS-configured symbol. The list
   * map (`symbolByCode`) is authoritative for every currency the app
   * knows about.
   */
  const formatPrice = useCallback(
    (amount: number, fromCurrency: string): string => {
      const symbolFor = (code: string): string =>
        symbolByCode[code.toUpperCase()] || code.toUpperCase();

      // Always render `<symbol> <amount>` with a single space so the
      // glyph never collides with the digit string — matches the spec
      // used by every other surface (emails, success page, booking
      // detail, payment page) for consistency.
      if (!fromCurrency) {
        return `${currencySymbol} ${amount.toFixed(2)}`;
      }

      const isSameCurrency =
        fromCurrency.toUpperCase() === currencyCode.toUpperCase();
      const hasRate =
        !isSameCurrency && (rates[fromCurrency.toUpperCase()] || rates[fromCurrency]);

      if (isSameCurrency) {
        // Already in user's currency — use the live `currencySymbol` state
        // so a mid-session currency change picks up the new glyph.
        return `${currencySymbol} ${amount.toFixed(2)}`;
      }

      if (hasRate) {
        // Convert and show in user's currency.
        return `${currencySymbol} ${convertPrice(amount, fromCurrency).toFixed(2)}`;
      }

      // No rate available — show in the original currency using the CMS
      // symbol for that code (e.g. "BHD 100.00" with the proper BHD glyph
      // from the DB instead of the ISO code).
      return `${symbolFor(fromCurrency)} ${amount.toFixed(2)}`;
    },
    [convertPrice, currencyCode, currencySymbol, rates, symbolByCode],
  );

  /**
   * Format an amount in `code`'s own currency using the CMS glyph, with NO
   * conversion. `formatPrice` re-converts any non-user currency to the user's
   * currency (when a rate exists), which is wrong for the "you'll pay in the
   * property's currency — approximately X" note: that amount must stay in the
   * property currency. `symbolByCode` is the same authoritative glyph map the
   * backend uses (`currencyService.getSymbol`), so e.g. EUR → "€", not "EUR".
   */
  const formatInCurrency = useCallback(
    (amount: number, code: string): string => {
      const symbol = symbolByCode[(code || '').toUpperCase()] || (code || 'GBP').toUpperCase();
      return `${symbol} ${(amount || 0).toFixed(2)}`;
    },
    [symbolByCode],
  );

  const value = useMemo(
    () => ({ currencyCode, currencySymbol, convertPrice, formatPrice, formatInCurrency, isReady, exchangeRates: rates }),
    [currencyCode, currencySymbol, convertPrice, formatPrice, formatInCurrency, isReady, rates],
  );

  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
}
